import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Container, Typography, Alert, CircularProgress,
  Paper, Skeleton, Button, Snackbar, Tooltip,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  TrendingUp, Business, Category, AccountBalance,
  Speed, Warning, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { icbAPI } from '../../services/api';

import { fmt } from './constants';
import FlowTab from './FlowTab';
import RankingsTab from './RankingsTab';
import PeersTab from './PeersTab';
import OpportunityMapTab from './OpportunityMapTab';
import SaturationTab from './SaturationTab';
import SectorDrawer from './SectorDrawer';

const ICBIndustry = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [level, setLevel] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [rankings, setRankings] = useState(null);
  const [peerTicker, setPeerTicker] = useState('');
  const [peerData, setPeerData] = useState(null);
  const [peerLoading, setPeerLoading] = useState(false);
  const [oppMap, setOppMap] = useState(null);
  const [saturation, setSaturation] = useState(null);

  const [expandedIcb, setExpandedIcb] = useState(null);
  const [leadersCache, setLeadersCache] = useState({});

  const [recalculating, setRecalculating] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const fetchedRef = useRef({});

  // --- Fetch helpers ---
  const doFetch = useCallback(async (key, fetcher) => {
    const cacheKey = `${key}_${level}`;
    if (fetchedRef.current[cacheKey]) return;
    fetchedRef.current[cacheKey] = true;
    try {
      setLoading(true);
      setError(null);
      await fetcher(level);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [level]);

  const loadRankings = useCallback(async (lvl) => {
    const res = await icbAPI.getRankings(lvl);
    setRankings(res.data);
  }, []);

  const loadOppMap = useCallback(async (lvl) => {
    const res = await icbAPI.getOpportunityMap(lvl);
    setOppMap(res.data);
  }, []);

  const loadSaturation = useCallback(async (lvl) => {
    const res = await icbAPI.getSaturation(lvl);
    setSaturation(res.data);
  }, []);

  useEffect(() => { fetchedRef.current = {}; }, [level]);

  useEffect(() => {
    if (activeTab <= 2) doFetch('rankings', loadRankings);
    else if (activeTab === 3) doFetch('oppMap', loadOppMap);
    else if (activeTab === 4) doFetch('saturation', loadSaturation);
  }, [activeTab, level, doFetch, loadRankings, loadOppMap, loadSaturation]);

  const fetchPeers = async (ticker) => {
    if (!ticker) return;
    try {
      setPeerLoading(true);
      const res = await icbAPI.getPeers(ticker, level);
      setPeerData(res.data);
    } catch (e) {
      setPeerData(null);
      setError(e.response?.data?.detail || e.message);
    } finally {
      setPeerLoading(false);
    }
  };

  const toggleLeaders = useCallback(async (icbCode) => {
    if (expandedIcb === icbCode) { setExpandedIcb(null); return; }
    setExpandedIcb(icbCode);
    if (leadersCache[icbCode]) return;
    setLeadersCache(prev => ({ ...prev, [icbCode]: { loading: true } }));
    try {
      const res = await icbAPI.getLeaders(icbCode, 30);
      setLeadersCache(prev => ({ ...prev, [icbCode]: { loading: false, data: res.data } }));
    } catch (e) {
      setLeadersCache(prev => ({
        ...prev,
        [icbCode]: { loading: false, error: e.response?.data?.detail || e.message },
      }));
    }
  }, [expandedIcb, leadersCache]);

  const handleRecalculate = useCallback(async () => {
    if (recalculating) return;
    setRecalculating(true);
    setError(null);
    try {
      const res = await icbAPI.calculate();
      const data = res.data || {};
      if (data.success) {
        setSnack({ open: true, severity: 'success', msg: t('icb.refreshDoneOk', { count: data.levels_calculated || 0, date: data.as_of_date || '-' }) });
        fetchedRef.current = {};
        setRankings(null); setOppMap(null); setSaturation(null);
        if (activeTab <= 2) await loadRankings(level);
        else if (activeTab === 3) await loadOppMap(level);
        else if (activeTab === 4) await loadSaturation(level);
      } else {
        setSnack({ open: true, severity: 'error', msg: t('icb.refreshDoneFail') });
      }
    } catch (e) {
      setSnack({ open: true, severity: 'error', msg: e.response?.data?.detail || e.message || t('icb.refreshDoneFail') });
    } finally {
      setRecalculating(false);
    }
  }, [recalculating, t, activeTab, level, loadRankings, loadOppMap, loadSaturation]);

  // --- Derived ---
  const items = useMemo(() => rankings?.rankings || [], [rankings]);
  const flowSorted = useMemo(() => [...items].sort((a, b) => (b.foreign_flow_score || 0) - (a.foreign_flow_score || 0)), [items]);
  const maxFlow = useMemo(() => Math.max(...items.map(i => i.foreign_flow_score || 0), 1), [items]);

  const topInflow = flowSorted[0];
  const outflow = useMemo(() => flowSorted.filter(i => (i.foreign_flow_score || 0) < 30), [flowSorted]);
  const strongCount = useMemo(() => items.filter(i => i.rating === 'STRONG' || i.rating === 'VERY_STRONG').length, [items]);
  const bestMom = useMemo(() => [...items].sort((a, b) => (b.momentum_score || 0) - (a.momentum_score || 0))[0], [items]);

  const scatterData = useMemo(() => {
    if (!oppMap?.quadrants) return [];
    const all = [];
    Object.values(oppMap.quadrants).forEach(qItems => {
      const arr = Array.isArray(qItems) ? qItems : (qItems?.data || []);
      arr.forEach(it => all.push({ ...it, label: it.name_vi || it.name_en }));
    });
    return all;
  }, [oppMap]);

  const tickerOptions = useMemo(() => {
    const tickers = new Set();
    items.forEach(i => {
      if (i.top_gainer) tickers.add(i.top_gainer);
      if (i.top_loser) tickers.add(i.top_loser);
    });
    return [...tickers].sort();
  }, [items]);

  // --- Nav card KPI helpers ---
  const buyCount = (() => {
    const b = oppMap?.quadrants?.BUY;
    if (!b) return null;
    return Array.isArray(b) ? b.length : (b?.data?.length ?? null);
  })();
  const satAlertCount = saturation?.data
    ? saturation.data.filter(s => ['HIGH', 'EXTREME'].includes(s.saturation_level)).length
    : null;

  const navItems = [
    {
      icon: <AccountBalance />,
      label: t('icb.tabs.moneyFlow'),
      kpi: topInflow
        ? t('icb.navCards.moneyFlowKpi', { name: topInflow.name_vi || topInflow.name_en || topInflow.icb_code, score: fmt(topInflow.foreign_flow_score, 0) })
        : t('icb.navCards.moneyFlowSub'),
      colorA: '#0ea5e9', colorB: '#0284c7',
    },
    {
      icon: <TrendingUp />,
      label: t('icb.tabs.rankings'),
      kpi: items.length > 0
        ? t('icb.navCards.rankingsKpi', { count: items.length, strong: strongCount })
        : t('icb.navCards.rankingsSub'),
      colorA: '#6366f1', colorB: '#4f46e5',
    },
    {
      icon: <Business />,
      label: t('icb.tabs.peers'),
      kpi: tickerOptions.length > 0
        ? t('icb.navCards.peersKpi', { count: tickerOptions.length })
        : t('icb.navCards.peersSub'),
      colorA: '#8b5cf6', colorB: '#7c3aed',
    },
    {
      icon: <Speed />,
      label: t('icb.tabs.opportunityMap'),
      kpi: buyCount != null ? t('icb.navCards.oppKpi', { count: buyCount }) : t('icb.navCards.oppSub'),
      colorA: '#f59e0b', colorB: '#d97706',
    },
    {
      icon: <Warning />,
      label: t('icb.tabs.saturation'),
      kpi: satAlertCount != null
        ? (satAlertCount > 0 ? t('icb.navCards.satAlert', { count: satAlertCount }) : t('icb.navCards.satSafe'))
        : t('icb.navCards.satSub'),
      colorA: '#ef4444', colorB: '#dc2626',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', py: 2 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%', px: { xs: 1, md: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)' }}>
              <Category sx={{ color: 'white', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('icb.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {items.length} {t('icb.subtitleSuffix')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>{t('icb.levelSelect')}</InputLabel>
              <Select value={level} label={t('icb.levelSelect')} onChange={(e) => setLevel(e.target.value)}>
                <MenuItem value={1}>{t('icb.level1')}</MenuItem>
                <MenuItem value={2}>{t('icb.level2')}</MenuItem>
                <MenuItem value={3}>{t('icb.level3')}</MenuItem>
                <MenuItem value={4}>{t('icb.level4')}</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={t('icb.refreshTooltip')} arrow>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={recalculating || loading}
                  onClick={handleRecalculate}
                  startIcon={recalculating
                    ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                    : <RefreshIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, px: 1.75, height: 40,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                    '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', boxShadow: '0 6px 16px rgba(99,102,241,0.45)' },
                    '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' },
                  }}
                >
                  {recalculating ? t('icb.refreshing') : t('icb.refresh')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Smart Nav Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.25, mb: 2.5 }}>
          {navItems.map((item, idx) => {
            const active = activeTab === idx;
            return (
              <Box
                key={idx}
                onClick={() => setActiveTab(idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveTab(idx); }}
                sx={{
                  cursor: 'pointer', px: 1.5, py: 1.25, borderRadius: 2.5,
                  background: active ? `linear-gradient(135deg, ${item.colorA} 0%, ${item.colorB} 100%)` : '#fff',
                  border: '1px solid', borderColor: active ? 'transparent' : '#e2e8f0',
                  boxShadow: active ? `0 8px 22px ${item.colorA}45, 0 2px 4px ${item.colorA}25` : '0 1px 2px rgba(15,23,42,0.04)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                  position: 'relative', overflow: 'hidden', outline: 'none',
                  '&:hover': active ? {} : { borderColor: item.colorA, transform: 'translateY(-2px)', boxShadow: `0 6px 14px ${item.colorA}22` },
                  '&:focus-visible': { outline: `2px solid ${item.colorA}`, outlineOffset: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: active ? 'rgba(255,255,255,0.22)' : `${item.colorA}15`,
                    color: active ? '#fff' : item.colorA, flexShrink: 0, transition: 'background-color 0.2s ease',
                  }}>
                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontWeight={700} noWrap sx={{ color: active ? '#fff' : '#0f172a', fontSize: '0.86rem', lineHeight: 1.25, letterSpacing: '-0.01em' }}>
                      {item.label}
                    </Typography>
                    <Typography noWrap sx={{ color: active ? 'rgba(255,255,255,0.88)' : '#64748b', fontSize: '0.72rem', display: 'block', mt: 0.25, fontWeight: 500 }}>
                      {item.kpi}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Loading skeleton */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
              {[1,2,3,4].map(i => (
                <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Skeleton variant="circular" width={34} height={34} />
                    <Skeleton variant="text" width={90} height={14} />
                  </Box>
                  <Skeleton variant="text" width={70} height={28} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={110} height={12} />
                </Paper>
              ))}
            </Box>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
              <Skeleton variant="text" width={180} height={20} sx={{ mb: 1.5 }} />
              {[1,2,3,4,5,6].map(i => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
                  <Skeleton variant="text" width={120} height={14} />
                  <Skeleton variant="rounded" width={`${30 + Math.random()*50}%`} height={18} sx={{ borderRadius: 1, flex: 1 }} />
                  <Skeleton variant="text" width={50} height={14} />
                </Box>
              ))}
            </Paper>
            <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Skeleton variant="text" width={160} height={18} />
              </Box>
              <Box sx={{ px: 1.5, py: 0.5 }}>
                {[1,2,3,4,5,6,7,8,9,10].map(i => (
                  <Box key={i} sx={{ display: 'flex', gap: 2, py: 1, borderBottom: '1px solid #fafafa' }}>
                    {[80,60,60,60,60,70,70,60].map((w,j) => <Skeleton key={j} variant="text" width={w} height={14} />)}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        ) : (
          <>
            {activeTab === 0 && (
              !items.length
                ? <Alert severity="info">{t('icb.common.noData')}</Alert>
                : <FlowTab
                    flowSorted={flowSorted}
                    maxFlow={maxFlow}
                    topInflow={topInflow}
                    outflow={outflow}
                    strongCount={strongCount}
                    bestMom={bestMom}
                    items={items}
                    rankings={rankings}
                    expandedIcb={expandedIcb}
                    toggleLeaders={toggleLeaders}
                  />
            )}

            {activeTab === 1 && (
              <RankingsTab
                items={items}
                rankings={rankings}
                expandedIcb={expandedIcb}
                toggleLeaders={toggleLeaders}
              />
            )}

            {activeTab === 2 && (
              <PeersTab
                peerTicker={peerTicker}
                setPeerTicker={setPeerTicker}
                peerData={peerData}
                peerLoading={peerLoading}
                fetchPeers={fetchPeers}
                tickerOptions={tickerOptions}
              />
            )}

            {activeTab === 3 && (
              <OpportunityMapTab
                scatterData={scatterData}
                oppMap={oppMap}
                expandedIcb={expandedIcb}
                toggleLeaders={toggleLeaders}
              />
            )}

            {activeTab === 4 && (
              <SaturationTab
                saturation={saturation}
                expandedIcb={expandedIcb}
                toggleLeaders={toggleLeaders}
                level={level}
              />
            )}
          </>
        )}
      </Container>

      <SectorDrawer
        expandedIcb={expandedIcb}
        setExpandedIcb={setExpandedIcb}
        items={items}
        leadersCache={leadersCache}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ICBIndustry;
